import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { FORMAT } from '@app/constants/formats';
import { EExperimentGroup, EPlanStatus, EPlanType } from '@app/swagger-types';
import { DAY_IN_MS } from '@app/utils/date.utils';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';

export const TRIAL_PRO_PLAN_NAME = 'Trial (Pro)';

export const useActivePlan = () => {
  const { currentUser } = useAuthorizedUser();
  const [, setRenderTrigger] = useState(0);

  const endOfSubscriptionDate =
    currentUser?.currentPlanDetails?.endOfCurrentSubscription ||
    currentUser?.currentPlanDetails?.endOfTrialCurrentSubscription;
  const scheduledUpdate = currentUser?.plan?.scheduledUpdate;

  const createdDate = currentUser?.createdDate;
  const providedReviewForTrialDate = currentUser?.providedReviewForTrialDate;

  useEffect(() => {
    if (!endOfSubscriptionDate) {
      return;
    }
    const expireDate = new Date(endOfSubscriptionDate);
    const delta = expireDate.getTime() - Date.now();
    const timeTillExpiration = delta < 0 ? 0 : delta;
    const timer = setTimeout(() => {
      setRenderTrigger((i) => i + 1);
    }, timeTillExpiration);
    return () => {
      clearTimeout(timer);
    };
  }, [endOfSubscriptionDate]);

  if (!currentUser || !createdDate) {
    return { isLoading: true };
  }

  const planDetails = currentUser.currentPlanDetails;
  const { plan } = currentUser;
  const { planType, planStatus } = planDetails || {};
  const isFreePlan = planType === EPlanType.FREE;
  const isTrial = planType === EPlanType.TRIAL || currentUser.plan?.name === TRIAL_PRO_PLAN_NAME; // TODO: Add after BE fix || currentUser.plan?.isTrial;
  const planCycle = plan?.price?.planCycle;
  const isPlanCanceled = planStatus === EPlanStatus.CANCELED;

  const now = Date.now();
  const trialStartDate = new Date(createdDate);
  const trialEndDate = isTrial && endOfSubscriptionDate ? new Date(endOfSubscriptionDate) : undefined;
  const totalTrialDays =
    trialEndDate && isTrial ? Math.round((trialEndDate.getTime() - trialStartDate.getTime()) / DAY_IN_MS) : 0;

  const daysSinceStart = Math.ceil((now - trialStartDate.getTime()) / DAY_IN_MS);
  const isTrialExtended = isTrial && Boolean(providedReviewForTrialDate);

  if (!endOfSubscriptionDate) {
    return {
      isLoading: true,
      isTrial,
      isFreePlan,
      planType,
      planStatus,
      planCycle,
      planDetails,
      isPlanCanceled,
      endOfSubscriptionDate,
      scheduledUpdate,
      totalTrialDays,
      daysSinceStart,
      isTrialExtended,
    };
  }

  const daysToExpire = isTrial
    ? totalTrialDays - daysSinceStart
    : Math.floor((new Date(endOfSubscriptionDate).getTime() - now) / DAY_IN_MS);
  const isTrialExpired = trialEndDate ? now >= trialEndDate.getTime() : false;

  const expireDate = new Date(endOfSubscriptionDate);
  const isExpired = new Date() >= expireDate;
  const isPlanCanceledStillActive = isPlanCanceled && !isExpired;
  const friendlyExpireDate = expireDate ? format(expireDate, FORMAT.DATE_YEAR) : undefined;
  const friendlyScheduledDate = scheduledUpdate?.nextPeriodStart
    ? format(new Date(scheduledUpdate.nextPeriodStart), FORMAT.DATE_YEAR)
    : undefined;

  const isPlanUnderChange = !currentUser || currentUser.currentSubscriptionId === currentUser.previousSubscriptionId;

  const isExperimentGroupA = currentUser.experimentGroup === EExperimentGroup.A;
  const isExperimentGroupB = currentUser.experimentGroup === EExperimentGroup.B;
  const isExperimentGroupC = currentUser.experimentGroup === EExperimentGroup.C;

  return {
    isExpired,
    isTrial,
    isFreePlan,
    isPlanCanceled,
    plan,
    planType,
    planStatus,
    planDetails,
    expireDate,
    friendlyExpireDate,
    isPlanCanceledStillActive,
    planCycle,
    endOfSubscriptionDate,
    scheduledUpdate,
    friendlyScheduledDate,
    daysToExpire,
    totalTrialDays,
    daysSinceStart,
    isTrialExpired,
    isTrialExtended,
    isPlanUnderChange,
    isExperimentGroupA,
    isExperimentGroupB,
    isExperimentGroupC,
  };
};
